import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const getToken = () => localStorage.getItem("slotmate_token");

  const setToken = (token) => {
    if (token) {
      localStorage.setItem("slotmate_token", token);
    } else {
      localStorage.removeItem("slotmate_token");
    }
  };

  const api = axios.create({
    baseURL: API_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const fetchUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      if (response.data.business) {
        setBusiness(response.data.business);
      }
      //Store subscription info
      if (response.data.subscription) {
        setSubscription(response.data.subscription);
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
      // NEW: Check if subscription expired
      if (error.response?.status === 403) {
        // Clear token and show message
        setToken(null);
      }
      setUser(null);
      setBusiness(null);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const requestMagicLink = async (email, role = "owner") => {
    const response = await api.post("/auth/magic-link", { email, role });
    return response.data;
  };

  const verifyMagicLink = async (token) => {
    const response = await api.post("/auth/verify", { token });
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const loginWithPassword = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    setToken(response.data.access_token);

    // Fetch full user data including subscription
    try {
      const meResponse = await api.get("/auth/me", {
        headers: { Authorization: `Bearer ${response.data.access_token}` },
      });
      setUser(meResponse.data);
      if (meResponse.data.business) {
        setBusiness(meResponse.data.business);
      }
      if (meResponse.data.subscription) {
        setSubscription(meResponse.data.subscription);
      }
    } catch (error) {
      // If subscription expired, this will throw 403
      if (error.response?.status === 403) {
        setToken(null);
        throw error; // Re-throw so LoginPage can show error
      }
      setUser(response.data.user);
    }
    return response.data;
  };

  const registerWithPassword = async (email, password) => {
    const response = await api.post("/auth/register", { email, password });
    setToken(response.data.access_token);

    // Fetch full user data including subscription (new users won't have business yet)
    try {
      const meResponse = await api.get("/auth/me", {
        headers: { Authorization: `Bearer ${response.data.access_token}` },
      });
      setUser(meResponse.data);
      if (meResponse.data.business) {
        setBusiness(meResponse.data.business);
      }
      if (meResponse.data.subscription) {
        setSubscription(meResponse.data.subscription);
      }
    } catch (error) {
      setUser(response.data.user);
    }

    return response.data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setBusiness(null);
    setSubscription(null);
  };

  const refreshBusiness = async () => {
    try {
      const response = await api.get("/businesses/my");
      setBusiness(response.data);
      // Also refresh subscription from /auth/me
      try {
        const meResponse = await api.get("/auth/me");
        if (meResponse.data.subscription) {
          setSubscription(meResponse.data.subscription);
        }
      } catch (e) {}
      return response.data;
    } catch (error) {
      console.error("Failed to fetch business:", error);
      return null;
    }
  };

  const value = {
    user,
    business,
    subscription,
    loading,
    isAuthenticated: !!user,
    requestMagicLink,
    verifyMagicLink,
    loginWithPassword,
    registerWithPassword,
    logout,
    refreshBusiness,
    api,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
